// Dispatch Desk — the dispatch admin's own dashboard: her queue by age and
// by reason, her dispatch speed vs last week, and today's volumes. The same
// measures the owners see on their cockpit, surfaced to her first. Every
// number opens its order list (shared drill modal) and exports CSV.

import { useEffect, useState } from 'react';
import { dispatchDeskApi } from '../api/services';
import InfoTip from '../components/InfoTip';
import DrillModal, { useDrill } from '../components/DrillModal';
import toast from 'react-hot-toast';
import { Loader2, Gauge, RefreshCcw, ArrowDown, ArrowUp, Minus } from 'lucide-react';

function AgeTile({ label, count, tone, info, onClick }) {
  const tones = {
    ok:    'border-emerald-200 bg-emerald-50/50',
    warn:  'border-amber-300 bg-amber-50/60',
    alarm: 'border-rose-300 bg-rose-50/60',
  };
  const text = {
    ok: 'text-emerald-700', warn: 'text-amber-700', alarm: 'text-rose-700',
  };
  return (
    <div className={`rounded-xl border p-4 ${count > 0 ? tones[tone] : 'border-gray-200 bg-white'}`}>
      <div className="flex items-center justify-between gap-1">
        <button onClick={onClick} title="See which orders these are"
                className="text-[11px] uppercase tracking-wide text-blue-600 hover:underline text-left">
          {label}
        </button>
        <InfoTip title={label}>{info}</InfoTip>
      </div>
      <button onClick={onClick} title="See which orders these are"
              className={`text-3xl font-semibold tabular-nums hover:underline ${count > 0 ? text[tone] : 'text-gray-300'}`}>
        {count}
      </button>
    </div>
  );
}

function SpeedDelta({ last7d, prior7d }) {
  if (last7d == null || prior7d == null) return null;
  const diff = last7d - prior7d;
  if (Math.abs(diff) < 0.5) {
    return <span className="inline-flex items-center gap-1 text-xs text-gray-500"><Minus className="w-3 h-3" /> flat vs prior week</span>;
  }
  const up = diff > 0; // slower = bad
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${up ? 'text-rose-600' : 'text-emerald-600'}`}>
      <Icon className="w-3 h-3" /> {Math.abs(diff).toFixed(1)}h {up ? 'slower' : 'faster'} than prior week
    </span>
  );
}

export default function DispatchDesk() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { drill, drillData, drillLoading, openDrill, exportDrill, closeDrill } = useDrill();

  function load() {
    setLoading(true);
    dispatchDeskApi.build()
      .then((r) => setData(r.data))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Gauge className="w-5 h-5 text-blue-600" />
          <h1 className="text-2xl font-semibold text-gray-900">Dispatch Desk</h1>
          <InfoTip title="What this dashboard is for" align="left">
            Your desk, measured the same way the owners' cockpit measures it —
            so you always see your numbers before they do. The age tiles are
            your to-do list by urgency; the by-reason table says <i>why</i>
            orders are waiting on you; speed shows your MBO→dispatch average
            against last week. Every number opens the exact orders and
            exports to CSV.
          </InfoTip>
        </div>
        <button onClick={load} disabled={loading}
                className="flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Your queue, your speed, today's flow — the owners see these same numbers.
      </p>

      {loading && !data && (
        <div className="py-16 text-center text-sm text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
        </div>
      )}

      {data && (
        <>
          <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">
            Waiting on you · <strong>{data.queue.total}</strong> order(s){' '}
            <InfoTip title="Waiting on you">
              Orders currently owned by the dispatch team, bucketed by how
              long since the last handoff. Under 24 hours is normal flow.
              Anything past 7 days shows red on the owners' cockpit too —
              clear those first.
            </InfoTip>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <AgeTile label="< 24h" count={data.queue.buckets.fresh_lt_24h} tone="ok"
                     onClick={() => openDrill('kalpana_queue', { bucket: 'fresh_lt_24h' })}
                     info="Fresh arrivals — normal flow. These only become a problem if they're still here tomorrow." />
            <AgeTile label="1-2 days" count={data.queue.buckets.aged_24_48h} tone="warn"
                     onClick={() => openDrill('kalpana_queue', { bucket: 'aged_24_48h' })}
                     info="Sitting a second day. Worth scanning for anything blocked that needs a decision rather than processing." />
            <AgeTile label="2-7 days" count={data.queue.buckets.aged_2_7d} tone="warn"
                     onClick={() => openDrill('kalpana_queue', { bucket: 'aged_2_7d' })}
                     info="Aging. Each of these usually has a reason (hold, OOS conversation, missing info) — the by-reason table below says which." />
            <AgeTile label="> 7 days" count={data.queue.buckets.aged_gt_7d} tone="alarm"
                     onClick={() => openDrill('kalpana_queue', { bucket: 'aged_gt_7d' })}
                     info="Red on the owners' cockpit. Clear or escalate these today — every one will be asked about." />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between gap-1">
                <button onClick={() => openDrill('dispatched_7d')} title="See the orders behind this average"
                        className="text-[11px] uppercase tracking-wide text-blue-600 hover:underline text-left">
                  My speed · MBO → dispatch
                </button>
                <InfoTip title="My speed">
                  Average hours from an order arriving from MBO to its
                  dispatch file being emailed to the vendor, over the last 7
                  days, compared to the 7 days before. This is the exact
                  number on the owners' cockpit. Click to see the orders
                  behind the average.
                </InfoTip>
              </div>
              <div className="text-3xl font-semibold text-gray-900 tabular-nums">
                {data.speed.last_7d != null ? `${data.speed.last_7d}h` : '—'}
              </div>
              <SpeedDelta last7d={data.speed.last_7d} prior7d={data.speed.prior_7d} />
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between gap-1">
                <button onClick={() => openDrill('orders_received_7d', { days: 1 })} title="See which orders these are"
                        className="text-[11px] uppercase tracking-wide text-blue-600 hover:underline text-left">
                  Received today
                </button>
                <InfoTip title="Received today">
                  Orders that arrived from MBO today. Your incoming workload —
                  click for the list.
                </InfoTip>
              </div>
              <button onClick={() => openDrill('orders_received_7d', { days: 1 })}
                      className="text-3xl font-semibold text-gray-900 tabular-nums hover:underline">
                {data.today.received}
              </button>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between gap-1">
                <button onClick={() => openDrill('dispatched_7d', { days: 1 })} title="See which orders these are"
                        className="text-[11px] uppercase tracking-wide text-blue-600 hover:underline text-left">
                  Dispatched today
                </button>
                <InfoTip title="Dispatched today">
                  Orders whose dispatch file was emailed to a vendor today.
                  If "received" keeps beating "dispatched", the queue grows.
                </InfoTip>
              </div>
              <button onClick={() => openDrill('dispatched_7d', { days: 1 })}
                      className="text-3xl font-semibold text-gray-900 tabular-nums hover:underline">
                {data.today.dispatched}
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-5">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold">
              Why orders are waiting on you{' '}
              <InfoTip title="Reading this table">
                Your queue grouped by the reason each order landed on your
                desk. "Approved — queued for dispatch" just needs processing;
                holds and OOS rows need conversations. "Oldest" is the worst
                case per reason — anything past 5 days is what the owners'
                stuck tile counts. Click a count for the orders, export to
                hand a list to whoever you're chasing.
              </InfoTip>
            </div>
            {data.queue.byReason.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                Nothing waiting on you. Clean desk.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="text-left px-3 py-2">Reason</th>
                    <th className="text-right px-3 py-2">Orders</th>
                    <th className="text-right px-3 py-2">Oldest</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.queue.byReason.map((r) => (
                    <tr key={r.reason_code} className="hover:bg-gray-50">
                      <td className="px-3 py-2">{r.reason_label || r.reason_code.replace(/_/g, ' ')}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => openDrill('kalpana_queue', { reason: r.reason_code })}
                                title="See which orders these are"
                                className="tabular-nums text-blue-600 hover:underline font-medium">
                          {r.count}
                        </button>
                      </td>
                      <td className={`px-3 py-2 text-right text-xs tabular-nums ${r.oldest_days > 5 ? 'text-rose-600 font-semibold' : 'text-gray-500'}`}>
                        {r.oldest_days}d
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between">
            <div className="flex items-center gap-1">
              <button onClick={() => openDrill('exceptions_7d')} title="See every exception with order and date"
                      className="text-sm text-blue-600 hover:underline">
                Exceptions handled this week: <strong>{data.exceptions7d}</strong>
              </button>
              <InfoTip title="Exceptions handled this week">
                The judgement calls you and the dispatch team made in the last
                7 days — holds, cancellations, OOS, reships, substitutes. This
                is the "Real exceptions handled" number on the owners'
                cockpit: your evidence of problems solved, not just orders
                pushed through.
              </InfoTip>
            </div>
            <span className="text-xs text-gray-400">as of {data.asOf}</span>
          </div>
        </>
      )}

      {drill && (
        <DrillModal data={drillData} loading={drillLoading}
                    onClose={closeDrill} onExport={exportDrill} />
      )}
    </div>
  );
}
